import React, { useState } from 'react';
import { Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-slate-900 text-slate-400 py-8 px-4 border-t border-slate-800" data-id="element-41">
        <div className="max-w-6xl mx-auto text-center" data-id="element-42">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-43">
            <Heart className="w-5 h-5 text-orange-400" data-id="element-44" />
            <p className="text-sm" data-id="element-45">
              Helping dogs and their owners find peace since 2024
            </p>
          </div>
          <div className="flex items-center justify-center gap-4 mb-4" data-id="element-46">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-slate-400 hover:text-orange-400 underline underline-offset-2 transition-colors cursor-pointer" data-id="element-47">
              Privacy Policy
            </button>
            <span className="text-slate-600" data-id="element-48">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-slate-400 hover:text-orange-400 underline underline-offset-2 transition-colors cursor-pointer" data-id="element-49">
              Terms of Service
            </button>
          </div>
          <p className="text-xs text-slate-500" data-id="element-50">
            © 2026 Paw-sitive Freedom Bundle. All rights reserved.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-51">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-52">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-53">
              <div className="flex items-center justify-between p-6 border-b border-slate-200" data-id="element-54">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-55">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-slate-100 transition-colors cursor-pointer" data-id="element-56">
                  <X className="w-5 h-5 text-slate-600" data-id="element-57" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-slate-700 text-sm leading-relaxed space-y-4" data-id="element-58">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-59" /> : <TermsContent data-id="element-60" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-slate-500" data-id="element-61">Last updated: April 29, 2026</p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-62">
        1. Information We Collect
      </h3>
      <p data-id="element-63">
        When you purchase The Paw-sitive Freedom Bundle, we collect only the
        information necessary to process your order and deliver your digital
        products. This includes your name, email address, and payment
        information. We do not collect phone numbers, physical addresses, or any
        other personal data unless voluntarily provided.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-64">
        2. How We Use Your Information
      </h3>
      <p data-id="element-65">
        Your information is used solely to process your purchase, deliver your
        digital products via email, and send occasional product updates or
        support communications. We will never sell, rent, or share your personal
        information with third parties for marketing purposes.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-66">
        3. Payment Security
      </h3>
      <p data-id="element-67">
        All transactions are processed through secure, PCI-compliant payment
        processors. We do not store your credit card information on our servers.
        Your financial data is encrypted and handled exclusively by our trusted
        payment partners.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-68">
        4. Cookies & Tracking
      </h3>
      <p data-id="element-69">
        We use essential cookies to ensure our website functions properly and
        analytics cookies to understand how visitors interact with our site. You
        can disable cookies in your browser settings at any time, though this
        may affect site functionality.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-70">
        5. Data Retention
      </h3>
      <p data-id="element-71">
        We retain your purchase information for as long as necessary to provide
        you with lifetime access to your digital products and to comply with
        legal obligations. You may request deletion of your data at any time by
        contacting our support team.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-72">6. Your Rights</h3>
      <p data-id="element-73">
        You have the right to access, correct, or delete your personal data. You
        may also opt out of marketing communications at any time by clicking the
        unsubscribe link in any email. We comply with applicable data protection
        regulations including GDPR and CCPA.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-74">
        7. Changes to This Policy
      </h3>
      <p data-id="element-75">
        We may update this Privacy Policy from time to time. Any changes will be
        posted on this page with an updated revision date. Continued use of our
        products after changes constitutes acceptance of the updated policy.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-slate-500" data-id="element-76">Last updated: April 29, 2026</p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-77">
        1. Product Description
      </h3>
      <p data-id="element-78">
        The Paw-sitive Freedom Bundle is a collection of digital educational
        materials (ebooks, PDFs, checklists, and guides) designed to help dog
        owners address separation anxiety in their pets. These materials are for
        informational and educational purposes only and do not constitute
        veterinary advice.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-79">
        2. Digital Delivery
      </h3>
      <p data-id="element-80">
        Upon successful payment, you will receive immediate access to all
        digital products via email and/or a secure download page. It is your
        responsibility to ensure your email address is correct at the time of
        purchase. All products are delivered in PDF format.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-81">
        3. 60-Day Money-Back Guarantee
      </h3>
      <p data-id="element-82">
        We offer a full 60-day money-back guarantee from the date of purchase.
        If you are not satisfied with the product for any reason, you may
        request a complete refund within 60 days. Refund requests should be sent
        to our support email. You may keep all downloaded materials regardless
        of refund status.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-83">
        4. Intellectual Property
      </h3>
      <p data-id="element-84">
        All content within The Paw-sitive Freedom Bundle is protected by
        copyright. You are granted a personal, non-transferable license to use
        the materials for your own purposes. You may not reproduce, distribute,
        sell, or share the materials with others without written permission.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-85">5. Disclaimer</h3>
      <p data-id="element-86">
        The techniques and advice presented in our materials are based on
        research and professional experience. However, every dog is unique, and
        results may vary. These materials are not a substitute for professional
        veterinary care. If your dog has severe behavioral issues, we recommend
        consulting with a licensed veterinarian or certified animal behaviorist.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-87">
        6. Limitation of Liability
      </h3>
      <p data-id="element-88">
        To the maximum extent permitted by law, we shall not be liable for any
        indirect, incidental, special, or consequential damages arising from the
        use of our products. Our total liability shall not exceed the amount
        paid for the product.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-89">7. Governing Law</h3>
      <p data-id="element-90">
        These terms shall be governed by and construed in accordance with the
        laws of the United States. Any disputes arising from these terms shall
        be resolved through binding arbitration.
      </p>

      <h3 className="text-lg font-semibold text-slate-900" data-id="element-91">
        8. Changes to Terms
      </h3>
      <p data-id="element-92">
        We reserve the right to modify these Terms of Service at any time.
        Changes will be effective immediately upon posting. Your continued use
        of the product after any changes constitutes acceptance of the new
        terms.
      </p>
    </>;
}